import React from "react";
import { MapPin, ArrowUpRight, Home, Signal } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { Button } from "./ui/button";
import { coveragePoints, siteConfig } from "../mock";

export default function Coverage() {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();

  return (
    <section id="copertura" className="py-20 lg:py-28 hsc-dots-soft">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center hsc-reveal ${
          visible ? "is-visible" : ""
        }`}
      >
        <div>
          <span className="text-primary font-semibold text-sm tracking-[0.18em] uppercase">
            {t.coverage.eyebrow}
          </span>
          <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[30px] sm:text-[40px] leading-tight tracking-[-0.02em]">
            {t.coverage.title}
          </h2>
          <p className="mt-5 text-[16px] leading-relaxed text-slate-700">{t.coverage.body}</p>

          <div className="mt-6 rounded-2xl bg-secondary/70 border border-slate-100 p-5 flex gap-4">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
              <Home className="w-6 h-6" strokeWidth={2.1} />
            </span>
            <p className="text-[14.5px] leading-relaxed text-slate-700">{t.coverage.body2}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-5">
            {t.coverage.points.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <span
                  className={`w-3.5 h-3.5 rounded-full ${
                    p.color === "active" ? "bg-primary" : "bg-emerald-400"
                  }`}
                />
                <span className="text-[13px] font-medium text-slate-700">{p.label}</span>
              </div>
            ))}
          </div>

          <Button
            asChild
            className="mt-7 rounded-full h-12 px-6 font-semibold group"
          >
            <a href={siteConfig.mapUrl} target="_blank" rel="noopener noreferrer">
              <MapPin className="w-4 h-4 mr-1" />
              {t.coverage.mapButton}
              <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </Button>
        </div>

        {/* stylised coverage map panel */}
        <div className="relative">
          <div className="relative rounded-[28px] overflow-hidden border border-slate-100 hsc-card-shadow aspect-[4/3] bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
            {/* faux terrain */}
            <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full opacity-70">
              <path d="M0,210 C60,180 120,220 190,195 C260,170 320,210 400,185 L400,300 L0,300 Z" fill="rgba(16,185,129,0.12)" />
              <path d="M0,240 C80,220 150,255 230,235 C300,218 360,245 400,230 L400,300 L0,300 Z" fill="rgba(13,148,168,0.14)" />
              <path d="M0,110 Q100,95 200,120 T400,110" fill="none" stroke="rgba(13,148,168,0.2)" strokeWidth="1.5" strokeDasharray="5 6" />
            </svg>

            {coveragePoints.map((pt) => (
              <div
                key={pt.id}
                className="absolute"
                style={{ left: `${pt.x}%`, top: `${pt.y}%`, transform: "translate(-50%,-50%)" }}
              >
                <span
                  className="absolute inset-0 m-auto w-4 h-4 rounded-full"
                  style={{
                    background: pt.status === "active" ? "rgba(13,148,168,0.35)" : "rgba(16,185,129,0.35)",
                    animation: "hsc-wave 2.6s ease-out infinite",
                    animationDelay: `${pt.id * 0.3}s`,
                  }}
                />
                <span
                  className={`relative grid place-items-center w-4 h-4 rounded-full ring-4 ring-white shadow ${
                    pt.status === "active" ? "bg-primary" : "bg-emerald-500"
                  }`}
                >
                  <Signal className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </span>
              </div>
            ))}

            <div className="absolute bottom-3 left-3 hsc-glass rounded-xl px-3 py-2 text-[11px] font-semibold text-slate-700 border border-white/60 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary" /> Costa Rei · Cagliari
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
